import { memo } from 'react';
import { Box } from '@mantine/core';

import { AppScrollArea } from '@/components/ui/AppScrollArea';

import type { WizardStepDescriptor } from './wizardStep';
import styles from './WizardStepRenderer.module.css';

interface WizardStepRendererProps<TValues, TRenderProps> {
  steps: WizardStepDescriptor<TValues, TRenderProps>[];
  /** 1-indexed position into `steps`. */
  currentStep: number;
  /** Prop bag passed to the active step's `render`. */
  renderProps: TRenderProps;
}

/**
 * Renders the active step from a descriptor list (`steps[currentStep-1].render`).
 * Shared by every wizard — features supply their own descriptors + prop bag.
 */
function WizardStepRendererInner<TValues, TRenderProps>({
  steps,
  currentStep,
  renderProps,
}: WizardStepRendererProps<TValues, TRenderProps>) {
  const descriptor = steps[currentStep - 1];

  // Self-scroll steps own their internal scroll (pinned header + scrollable
  // list). They get a bounded flex-column instead of the shared outer area, so
  // their inner `flex:1; min-height:0` regions can size and scroll on their own.
  if (descriptor?.ownsScroll) {
    return (
      <div className={styles.stepFill}>{descriptor.render(renderProps)}</div>
    );
  }

  return (
    <AppScrollArea fill fullBleedGutter="md">
      <div className={styles.stepContainerInner}>
        <Box className={styles.stepContent}>
          {descriptor?.render(renderProps)}
        </Box>
      </div>
    </AppScrollArea>
  );
}

// `memo` drops generic params — cast restores them for callers.
export const WizardStepRenderer = memo(
  WizardStepRendererInner
) as typeof WizardStepRendererInner;
